import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api, { fmtEUR } from '@/lib/api';
import { useCurrency } from '@/lib/currency';
import useSEO from '@/hooks/useSEO';
import ReviewsSection from '@/components/ReviewsSection';
import SmartImage from '@/components/SmartImage';
import { ProductRail } from '@/components/ProductRail';
import { WaveBicoloreShowcase } from '@/components/WaveBicoloreShowcase';

const EditorialBlock = ({ overline, title, body, image, cta, ctaLink, reverse, testId, tone, containImageOnMobile = false }) => (
  <section className={`py-16 md:py-32 ${tone}`} data-testid={testId}>
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
      <div className={`${reverse ? 'md:order-2' : ''}`}>
        <div className="jewelry-media-surface overflow-hidden">
          <img
            src={image}
            alt={title}
            className={`w-full h-40 sm:h-48 md:h-[70vh] ${containImageOnMobile ? 'object-contain md:object-cover bg-white' : 'object-cover'} editorial-img`}
            style={{ filter: 'saturate(0.92)' }}
            data-testid={`${testId}-image`}
          />
        </div>
      </div>
      <div className={`${reverse ? 'md:order-1' : ''} md:px-8`}>
        <div className="overline text-[#C9A961] mb-6 editorial-overline">{overline}</div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] text-[#0F3530] mb-8 whitespace-pre-line">
          {title}
        </h2>
        <div className="divider-gold mb-8" />
        <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light max-w-md mb-10 whitespace-pre-line">
          {body}
        </p>
        {cta && ctaLink && (
          <Link to={ctaLink} className="btn-outline-petrol inline-flex items-center gap-3" data-testid={`cta-${ctaLink.replace(/\//g, '')}`}>
            {cta} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  </section>
);

/** Elegant luxury loading state — dark petrol background + slow shimmer
 *  Renders while /api/site-settings and /api/products are loading.
 *  Prevents any flash of fallback/static content. */
const HomeSkeleton = ({ lang }) => (
  <div className="min-h-screen bg-white" data-testid="home-skeleton">
    {/* Hero skeleton: deep petrol gradient + animated gold accent */}
    <section
      className="relative h-screen w-full overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at center, #E7C9C6 0%, #C58F9B 100%)' }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <img src="/brand/harizi-diamond-favicon.png?v=20261018" alt="Harizi" className="h-20 w-20 animate-pulse object-contain" data-testid="home-loading-diamond-logo" />
        <div className="mt-8 h-px w-12 bg-[#C9A961] opacity-60 animate-pulse" />
        <div className="mt-10 overline text-white/40">
          {lang === 'en' ? 'Loading atelier...' : 'Caricamento atelier...'}
        </div>
      </div>
    </section>
  </div>
);

export default function Home() {
  const { t, i18n } = useTranslation();
  const { format: fmtC } = useCurrency();
  const [loaded, setLoaded] = useState(false);
  const [featured, setFeatured] = useState([]);
  const [waveBicolore, setWaveBicolore] = useState([]);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroMobileImageUrl, setHeroMobileImageUrl] = useState(null);
  const [heroVideoUrl, setHeroVideoUrl] = useState(null);
  const [heroTextVisible, setHeroTextVisible] = useState(false);
  const [heroBackgroundColor, setHeroBackgroundColor] = useState('#FFFFFF');
  const [heroTitle, setHeroTitle] = useState(null);
  const [heroSubtitle, setHeroSubtitle] = useState(null);
  const [sections, setSections] = useState([]);
  const categoryRailRef = useRef(null);

  useSEO({
    title: i18n.language === 'it'
      ? 'Harizi Gioielli — Jewelry as Legacy | Alta Gioielleria Italiana'
      : 'Harizi Gioielli — Jewelry as Legacy | Italian High Jewelry',
    description: i18n.language === 'it'
      ? 'Alta gioielleria italiana su misura. Gemme rare certificate (zaffiri Ceylon, rubini Burma, padparadscha), anelli di fidanzamento e creazioni esclusive. Concierge dedicato.'
      : 'Italian bespoke high jewelry. Certified rare gems (Ceylon sapphires, Burmese rubies, padparadscha), engagement rings and exclusive creations. Dedicated concierge.',
  });

  useEffect(() => {
    let mounted = true;
    // Wait for BOTH endpoints to complete before un-blanking the page.
    Promise.allSettled([
      api.get('/products', { params: { featured: true } }),
      api.get('/products', { params: { category: 'rings', wave_bicolore_featured: true } }),
      api.get('/site-settings'),
    ]).then(([prodResult, waveResult, settingsResult]) => {
      if (!mounted) return;
      if (prodResult.status === 'fulfilled') {
        setFeatured(prodResult.value.data.slice(0, 3));
      }
      if (waveResult.status === 'fulfilled') {
        setWaveBicolore(waveResult.value.data.slice(0, 4));
      }
      if (settingsResult.status === 'fulfilled') {
        const data = settingsResult.value.data;
        setHeroImageUrl(data?.hero_image_url || null);
        setHeroMobileImageUrl(data?.hero_mobile_image_url || null);
        setHeroVideoUrl(data?.hero_video_url || null);
        setHeroTextVisible(data?.hero_text_visible === true);
        setHeroBackgroundColor(/^#[0-9A-Fa-f]{6}$/.test(data?.hero_background_color || '') ? data.hero_background_color : '#FFFFFF');
        setHeroTitle(data?.hero_title || null);
        setHeroSubtitle(data?.hero_subtitle || null);
        setSections(Array.isArray(data?.home_sections) ? data.home_sections : []);
      }
      setLoaded(true);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const rail = categoryRailRef.current;
    const isMobile = window.matchMedia('(max-width: 767px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!loaded || !rail || !isMobile.matches || reduceMotion.matches) return undefined;

    let direction = 1;
    let pauseUntil = 0;

    const pauseAutoScroll = () => {
      pauseUntil = performance.now() + 4500;
    };
    const advance = () => {
      if (performance.now() <= pauseUntil) return;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      if (maxScroll <= 0) return;
      if (rail.scrollLeft >= maxScroll - 1) direction = -1;
      if (rail.scrollLeft <= 1) direction = 1;
      rail.scrollLeft = Math.max(0, Math.min(maxScroll, rail.scrollLeft + direction));
    };

    rail.addEventListener('touchstart', pauseAutoScroll, { passive: true });
    rail.addEventListener('pointerdown', pauseAutoScroll);
    rail.addEventListener('wheel', pauseAutoScroll, { passive: true });
    const intervalId = window.setInterval(advance, 50);

    return () => {
      window.clearInterval(intervalId);
      rail.removeEventListener('touchstart', pauseAutoScroll);
      rail.removeEventListener('pointerdown', pauseAutoScroll);
      rail.removeEventListener('wheel', pauseAutoScroll);
    };
  }, [loaded]);

  const lang = i18n.language === 'en' ? 'en' : 'it';
  const getField = (s, base) => s?.[`${base}_${lang}`] || s?.[`${base}_it`] || '';
  const desktopHeroImageUrl = heroImageUrl || heroMobileImageUrl;
  const liveHeroTitle = (heroTitle && (heroTitle[lang] || heroTitle.it)) || t('hero.title');
  const liveHeroSubtitle = (heroSubtitle && (heroSubtitle[lang] || heroSubtitle.it)) || t('hero.subtitle');
  if (!loaded) return <HomeSkeleton lang={lang} />;

  return (
    <div className="bg-white" data-testid="home-page">
      {/* HERO — image backdrop with title & subtitle only (no CTA buttons, no Scroll) */}
      <section className="relative h-screen w-full overflow-hidden" style={{ backgroundColor: heroBackgroundColor }} data-testid="hero">
        {heroVideoUrl ? (
          <video
            src={heroVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-contain object-center"
            style={{ objectFit: 'contain', objectPosition: 'center' }}
          />
        ) : desktopHeroImageUrl ? (
          <SmartImage
            src={desktopHeroImageUrl}
            mobileSrc={heroMobileImageUrl || undefined}
            alt="Harizi"
            priority
            sizes="100vw"
            pictureClassName="absolute inset-0 block h-full w-full overflow-hidden"
            className="absolute inset-0 block h-full w-full object-contain object-center"
            style={{ objectFit: 'contain', objectPosition: 'center' }}
            data-testid="hero-responsive-image"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(ellipse at center, #E7C9C6 0%, #C58F9B 100%)' }}
          />
        )}
        {heroTextVisible && (
          <div className="relative z-10 flex h-full -translate-y-8 flex-col items-center justify-center px-6 py-24 text-center text-white md:-translate-y-12" data-testid="hero-content">
            <div className="overline text-[#C9A961] mb-6 fade-up" data-testid="hero-overline">{t('hero.overline')}</div>
            <h1 className="mx-auto w-full max-w-5xl text-center font-serif text-5xl font-light leading-[0.95] tracking-tight fade-up-delay-1 md:text-7xl lg:text-8xl whitespace-pre-line" data-testid="hero-title">{liveHeroTitle}</h1>
            <div className="divider-gold mt-7 mb-5 fade-up-delay-2" style={{ width: 60 }} data-testid="hero-divider" />
            <p className="max-w-xl text-base font-light leading-relaxed text-white/85 fade-up-delay-2 md:text-lg whitespace-pre-line" data-testid="hero-subtitle">{liveHeroSubtitle}</p>
          </div>
        )}
      </section>

      {/* Scopri per categoria — 5 tiles, overlapping the hero bottom edge */}
      <section
        className="relative z-20 bg-white pb-8 md:pb-32"
        data-testid="section-categories"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div
            ref={categoryRailRef}
            className="home-category-rail flex flex-nowrap gap-3 overflow-x-auto md:grid md:grid-cols-5 md:gap-x-6 md:gap-y-10 md:overflow-visible"
            data-testid="home-category-carousel"
            aria-label={lang === 'it' ? 'Categorie Harizi' : 'Harizi categories'}
          >
            {[
              { to: '/anelli-wave',         img: '/categories/wave.png',           label: t('home_categories.wave'),       testId: 'cat-wave',       desktopOrder: 'md:order-3' },
              { to: '/anelli-fidanzamento', img: '/categories/engagement.png',     label: t('home_categories.engagement'), testId: 'cat-engagement', desktopOrder: 'md:order-1' },
              { to: '/fedi-nuziali',        img: '/categories/wedding_bands.png',  label: t('home_categories.wedding'),    testId: 'cat-wedding',    desktopOrder: 'md:order-2' },
              { to: '/pronta-consegna',     img: '/categories/private.png',        label: t('home_categories.private'),    testId: 'cat-private',    desktopOrder: 'md:order-4' },
              { to: '/atelier',             img: '/categories/create.png',         label: t('home_categories.create'),     testId: 'cat-create',     desktopOrder: 'md:order-5' },
            ].map((c) => (
              <Link
                to={c.to}
                key={c.to}
                data-testid={c.testId}
                className={`home-category-card group flex flex-col items-center text-center ${c.desktopOrder}`}
              >
                <div className="w-full h-28 sm:h-32 md:h-auto md:aspect-square flex items-center justify-center overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.label}
                    loading="lazy"
                    className="w-full h-full scale-[1.3] md:w-4/5 md:h-4/5 md:scale-100 object-contain transition-transform duration-700 group-hover:scale-[1.35] md:group-hover:scale-105 mix-blend-multiply"
                  />
                </div>
                <div className="mt-4 font-serif kerning-wide text-[13px] md:text-[12px] uppercase tracking-[0.18em] md:tracking-[0.22em] text-[#3B2A1E] group-hover:text-[#C9A961] transition-colors duration-500 whitespace-pre-line leading-relaxed">
                  {c.label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <WaveBicoloreShowcase language={lang} products={waveBicolore} variant="home" />

      <ProductRail
        cardTestIdPrefix="featured"
        carouselTestId="featured-products-carousel"
        eyebrow={lang === 'it' ? 'Selezione' : 'Selection'}
        formatPrice={fmtC}
        lang={lang}
        products={featured}
        sectionTestId="section-featured"
        title={lang === 'it' ? 'Eccellenze Preziose' : 'Treasured Excellence'}
        surfaceClass="bg-white"
      />

      {/* 4 Editorial sections — only render with image set */}
      {sections.filter((s) => !!s.image_url).map((s, idx) => (
        <EditorialBlock
          key={s.id || idx}
          testId={`section-${s.id || idx}`}
          overline={getField(s, 'overline')}
          title={getField(s, 'title')}
          body={getField(s, 'body')}
          image={s.image_url}
          cta={getField(s, 'cta')}
          ctaLink={s.link}
          reverse={idx % 2 === 1}
          tone={idx % 2 === 0 ? 'bg-white' : 'bg-[#E7C9C6]'}
          containImageOnMobile={s.id === 'contatti'}
        />
      ))}

      {/* Certifications */}
      <ReviewsSection lang={lang} />

      <section className="py-24 bg-white" data-testid="section-certifications">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <div className="overline text-[#C9A961] mb-4">{t('sections.certificazioni.overline')}</div>
          <h2 className="font-serif text-3xl md:text-4xl font-light text-[#0F3530] mb-10">
            {t('sections.certificazioni.title')}
          </h2>
          <div className="flex items-center justify-center opacity-70">
            <div className="font-serif text-2xl text-[#0F3530] tracking-[0.2em]" data-testid="certification-cisgem">CISGEM</div>
          </div>
          <p className="text-sm text-[#4A4A4A] max-w-2xl mx-auto mt-10 leading-relaxed">
            {lang === 'it'
              ? 'La nostra promessa di trasparenza è garantita dai laboratori più autorevoli.'
              : 'Our promise of transparency is backed by the most authoritative laboratories.'}
          </p>
        </div>
      </section>
    </div>
  );
}
